
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';

type ApiSettings = Tables<'api_settings'>;

interface ApiSettingsManagerProps {
  settings: ApiSettings | null;
  onSettingsUpdate: (settings: ApiSettings) => void;
}

const ApiSettingsManager = ({ settings, onSettingsUpdate }: ApiSettingsManagerProps) => {
  const [isSaving, setIsSaving] = useState(false);

  const updateSettings = async (newSettings: Partial<ApiSettings>) => {
    if (!settings) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('api_settings')
        .update(newSettings)
        .eq('id', settings.id);

      if (error) {
        toast.error('Failed to update settings');
        return;
      }

      const updatedSettings = { ...settings, ...newSettings };
      onSettingsUpdate(updatedSettings);
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Error updating settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (!settings) {
    return <div>Failed to load API settings</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Submission Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="auto-approve">Auto-approve submissions</Label>
            <p className="text-sm text-muted-foreground">
              When enabled, API submissions with high confidence scores will be automatically approved.
              When disabled, all API submissions will go to pending state for manual review.
            </p>
          </div>
          <Switch
            id="auto-approve"
            checked={settings.auto_approve_enabled}
            onCheckedChange={(checked) => 
              updateSettings({ auto_approve_enabled: checked })
            }
            disabled={isSaving}
          />
        </div>

        {settings.auto_approve_enabled && (
          <div className="space-y-2">
            <Label htmlFor="threshold">Auto-approval confidence threshold</Label>
            <p className="text-sm text-muted-foreground">
              Submissions with confidence scores above this threshold will be auto-approved (0.0 - 1.0)
            </p>
            <input
              id="threshold"
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={settings.auto_approve_threshold}
              onChange={(e) => 
                updateSettings({ auto_approve_threshold: parseFloat(e.target.value) })
              }
              disabled={isSaving}
              className="w-32 px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        )}

        <div className="mt-4 p-4 bg-muted rounded-lg">
          <h4 className="font-semibold mb-2">Current Behavior</h4>
          <p className="text-sm">
            {settings.auto_approve_enabled 
              ? `API submissions with confidence ≥ ${settings.auto_approve_threshold} will be auto-approved. Others will be pending.`
              : 'All API submissions will require manual admin approval.'
            }
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ApiSettingsManager;
